import React, { useMemo, useCallback } from 'react';
import { pick } from 'js/utils/ramda.util';
import { Icon } from 'js/components/common/Icon';
import { useFormatter } from 'js/hooks/useFormatter';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import { useVisualDates } from './useVisualDates';
import { useDynamicReducerSelector } from '../store';

import style from '../DetailedTransactionHistory.scss';

export const useSummary = () => {
    const translate = useComponentTranslations();
    const formatter = useFormatter();
    const {
        summary,
        summaryError,
        transactions,
        endingBalances,
        startingBalances
    } = useDynamicReducerSelector(pick([
        'summary',
        'summaryError',
        'transactions',
        'endingBalances',
        'startingBalances'
    ]));
    const { showTotalTransactionHistory, showBeginEndBalanceEnabled } = useComponentConfig();
    const { startTime, endTime } = useVisualDates();
    const startDate = formatter.date(startTime);
    const endDate = formatter.date(endTime);
    const totalPeriod = translate('total.period', [startDate, endDate]);
    const startEndPeriod = translate('startend.period', [startDate, endDate]);

    const getAccordionCell = useCallback((name, value) => (
        <>
            <div className="accordionBodyCellName">{translate(name)}</div>
            <div className="accordionBodyCellValue">{value}</div>
        </>
    ), [translate]);

    const getAccordionHeader = useCallback(({ type }) => (
        <>
            <Icon type="summaryTransactions" />
            <div className="accordionHeaderSmallDetails">
                <span className={style.highlight}>{translate(`${type}.title.period`)}</span>
                <span>
                    {type === 'total' ? totalPeriod : startEndPeriod}
                </span>
            </div>
        </>
    ), [translate, totalPeriod, startEndPeriod]);

    return useMemo(() => {
        if (!transactions.length || !showTotalTransactionHistory && !showBeginEndBalanceEnabled || summaryError) {
            return null;
        }

        const tableSummary = [];
        const { totalWins, totalBets, totalDeposits, totalApprovedWithdraws } = summary;
        const accountSummary = {
            id: 1,
            isExpandedByDefault: true,
            tableView: {
                headerCells: [
                    { id: 0, content: translate('total.title.period'), className: style.summaryPeriod },
                    { id: 1, content: translate('total.title.wins') },
                    { id: 2, content: translate('total.title.bets') },
                    { id: 3, content: translate('total.title.deposits') },
                    { id: 4, content: translate('total.title.withdraws'), className: style.narrowColumn }
                ],
                rowCells: [
                    { id: 0, content: totalPeriod, className: `${style.summaryPeriod} ${style.highlight}` },
                    { id: 1, content: totalWins },
                    { id: 2, content: totalBets },
                    { id: 3, content: totalDeposits },
                    { id: 4, content: totalApprovedWithdraws, className: style.narrowColumn }
                ]
            },
            accordionView: {
                headerCells: [{
                    id: 0,
                    className: style.summaryAccordionHeader,
                    content: getAccordionHeader({ type: 'total' })
                }],
                rowCells: [
                    { id: 0, content: getAccordionCell('total.title.wins', totalWins) },
                    { id: 1, content: getAccordionCell('total.title.bets', totalBets) },
                    { id: 2, content: getAccordionCell('total.title.deposits', totalDeposits) },
                    { id: 3, content: getAccordionCell('total.title.withdraws', totalApprovedWithdraws) }
                ]
            }
        };
        const startEndSummary = {
            id: 2,
            isExpandedByDefault: true,
            tableView: {
                headerCells: [
                    { id: 0, content: translate('startend.title.period'), className: style.summaryPeriod },
                    { id: 1, content: translate('startend.title.starting') },
                    { id: 2, content: translate('startend.title.ending'), className: style.summaryEnding }
                ],
                rowCells: [
                    { id: 0, content: startEndPeriod, className: `${style.summaryPeriod} ${style.highlight}` },
                    { id: 1, content: startingBalances },
                    { id: 2, content: endingBalances, className: style.summaryEnding }
                ]
            },
            accordionView: {
                headerCells: [{
                    id: 0,
                    className: style.summaryAccordionHeader,
                    content: getAccordionHeader({ type: 'startend' })
                }],
                rowCells: [
                    { id: 0, content: getAccordionCell('startend.title.starting', startingBalances) },
                    { id: 1, content: getAccordionCell('startend.title.ending', endingBalances) }
                ]
            }
        };

        if (showTotalTransactionHistory) {
            tableSummary.push(accountSummary);
        }

        if (showBeginEndBalanceEnabled) {
            tableSummary.push(startEndSummary);
        }

        return tableSummary;
    }, [
        translate, totalPeriod, startEndPeriod, summary, showTotalTransactionHistory,
        endingBalances, startingBalances, summaryError, showBeginEndBalanceEnabled,
        getAccordionHeader, getAccordionCell, transactions
    ]);
};
